import React from 'react';

import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import Table from '../../../../Table';
import Button from '../../../../Button';

import LenderService from '../../../../../services/LenderService';
import LenderContactService from '../../../../../services/LenderContactService';
import { useUiContext } from '../../../../../context/UiContext';
import useUserRole from '../../../../../hooks/useUserRole';

export default function Loans({ lenderId }) {
  const { showDialogModal, showApiErrorToast, showSuccessToast } =
    useUiContext();

  const navigate = useNavigate();

  const { isSuperAdmin } = useUserRole();

  const {
    data: lenderContacts,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['lenderContacts', lenderId],
    queryFn: () => LenderService.getLenderContacts(lenderId),
  });

  const { mutate: deleteLenderContact } = useMutation({
    mutationFn: (contactId) =>
      LenderContactService.deleteLenderContact(contactId),
    onSuccess: () => {
      showSuccessToast('Contact deleted successfully');
      refetch();
    },
    onError: (error) => {
      showApiErrorToast(error);
    },
  });

  const cols = [
    {
      title: 'First name',
      keys: ['first_name'],
      link: (row) => `/lenders/${row.lender_id}/contacts/${row.id}?tab=Contact`,
    },
    {
      title: 'Last name',
      keys: ['last_name'],
      link: (row) => `/lenders/${row.lender_id}/contacts/${row.id}?tab=Contact`,
    },
    {
      title: 'Signer',
      keys: ['is_signer'],
      type: 'checkbox',
      isSelected: (row) => row.is_signer,
    },
    {
      title: '',
      type: 'delete',
      isVisible: () => isSuperAdmin,
      onClick: (row) => {
        showDialogModal({
          title: 'Delete Contact',
          message: `Are you sure you want to delete ${row.first_name} ${row.last_name}?`,
          onConfirm: () => deleteLenderContact(row.id),
          type: 'danger',
          confirmButtonLabel: 'Yes, delete',
          cancelButtonLabel: 'No, cancel',
        });
      },
    },
  ];

  return (
    <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
      <div className="flex justify-between mb-4 flex-col sm:flex-row gap-4">
        <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold">
          Lender Contacts
        </h2>
        <Button
          className="md:!max-w-[200px]"
          onClick={() => {
            navigate(`/lenders/${lenderId}/contacts/new?tab=Contact`);
          }}
        >
          <svg
            className="w-4 h-4 fill-current opacity-50 shrink-0"
            viewBox="0 0 16 16"
          >
            <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
          </svg>
          <span className="ml-2">Create Contact</span>
        </Button>
      </div>
      <div className="flex">
        {/* Content area */}

        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          <div className="w-full max-w-9xl mx-auto">
            <Table
              rows={lenderContacts}
              cols={cols}
              title="Contacts"
              isFetching={isFetching}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
